import OldQueries from './quick-queries-old';
import { getRecordedFeatureFlag } from './getRecordedFeatureFlag';

const memberFieldsDefault = [
  'id',
  'aaId',
  'activityBlocked',
  'avatarHash',
  'avatarUrl',
  'bio',
  'bioData',
  'confirmed',
  'fullName',
  'goldSunsetFreeTrialIdOrganization',
  'idEnterprise',
  'idEnterprisesDeactivated',
  'idMemberReferrer',
  'idPremOrgsAdmin',
  'initials',
  'memberType',
  'nonPublic',
  'nonPublicAvailable',
  'products',
  'url',
  'username',
  'status',
  'aaBlockSyncUntil',
  'aaEmail',
  'aaEnrolledDate',
  'avatarSource',
  'credentialsRemovedCount',
  'domainClaimed',
  'email',
  'goldSunsetFreeTrialIdOrganization',
  'gravatarHash',
  'idBoards',
  'idOrganizations',
  'idEnterprisesAdmin',
  'limits',
  'loginTypes',
  'marketingOptIn',
  'messagesDismissed',
  'oneTimeMessagesDismissed',
  'prefs',
  'trophies',
  'uploadedAvatarHash',
  'uploadedAvatarUrl',
  'premiumFeatures',
  'isAaMastered',
  'ixUpdate',
];

const boardFieldsMinimal = [
  'name',
  'closed',
  'dateLastActivity',
  'dateLastView',
  'datePluginDisable',
  'enterpriseOwned',
  'idOrganization',
  'prefs',
  'premiumFeatures',
  'shortLink',
  'shortUrl',
  'url',
  'creationMethod',
  'idEnterprise',
].join(',');

// Used for extremely large board fetches
// dateLastView is not on the board schema which forces us to read from the views collection
// omitting for perf reasons
const boardFieldsExtraMinimal = [
  'name',
  'closed',
  'dateLastActivity',
  'datePluginDisable',
  'enterpriseOwned',
  'idOrganization',
  'prefs',
  'premiumFeatures',
  'shortLink',
  'shortUrl',
  'url',
  'creationMethod',
  'idEnterprise',
].join(',');

const boardFieldsFull = [
  boardFieldsMinimal,
  'desc',
  'descData',
  'idTags',
  'labelNames',
  'limits',
  'memberships',
  'powerUps',
  'subscribed',
  'templateGallery',
].join(',');

const boardFieldsMinimalSubscribed = [boardFieldsMinimal, 'subscribed'].join(
  ',',
);

const memberFields = [
  'activityBlocked',
  'avatarUrl',
  'bio',
  'bioData',
  'confirmed',
  'fullName',
  'idEnterprise',
  'idMemberReferrer',
  'initials',
  'memberType',
  'nonPublic',
  'products',
  'url',
  'username',
].join(',');

const memberFieldsAndPremOrgsAdmin = [memberFields, 'idPremOrgsAdmin'].join(
  ',',
);

const organizationFieldsMinimal = [
  'id',
  'name',
  'displayName',
  'products',
  'prefs',
  'premiumFeatures',
  'logoHash',
  'idEnterprise',
  'limits',
  'credits',
].join(',');

const organizationFieldsMinimalMemberships = [
  organizationFieldsMinimal,
  'memberships',
].join(',');

const cardFieldsMinimal = [
  'id',
  'badges',
  'closed',
  'dueComplete',
  'dateLastActivity',
  'desc',
  'descData',
  'due',
  'dueReminder',
  'email',
  'idBoard',
  'idChecklists',
  'idList',
  'idMembers',
  'idMembersVoted',
  'idShort',
  'idAttachmentCover',
  'labels',
  'idLabels',
  'manualCoverAttachment',
  'name',
  'pos',
  'shortLink',
  'shortUrl',
  'start',
  'subscribed',
  'url',
  'cover',
  'isTemplate',
  'cardRole',
];

const cardFieldsBulk = [
  'badges',
  'cardRole',
  'closed',
  'dateLastActivity',
  'desc',
  'descData',
  'due',
  'dueComplete',
  'dueReminder',
  'idAttachmentCover',
  'idList',
  'idBoard',
  'idMembers',
  'idShort',
  'idLabels',
  'limits',
  'name',
  'pos',
  'shortUrl',
  'shortLink',
  'subscribed',
  'url',
  'locationName',
  'address',
  'coordinates',
  'cover',
  'isTemplate',
  'start',
].join(',');

const boardFieldsInOrganization = [boardFieldsMinimal, 'idTags'].join(',');
const boardFieldsInLargeOrganization = [boardFieldsExtraMinimal, 'idTags'].join(
  ',',
);

const organizationBoardsFields = [
  organizationFieldsMinimalMemberships,
  'desc',
  'descData',
  'website',
  'billableCollaboratorCount',
].join(',');

// const enterpriseFields = [
//   'id',
//   'accessEnabled',
//   'atlOrgDisplayName',
//   'atlOrgId',
//   'displayName',
//   'enterpriseDomains',
//   'hasAtlassianAccess',
//   'hasClaimedDomains',
//   'idAdmins',
//   'idPluginsAllowed',
//   'isAtlassianOrg',
//   'isEnterpriseWithoutSSO',
//   'isRealEnterprise',
//   'logoHash',
//   'logoUrl',
//   'name',
//   'organizationPrefs',
//   'pluginWhitelistingEnabled',
//   'prefs',
//   'ssoActivationFailed',
//   'ssoDateDelayed',
// ];

const paidAccountFieldsMinimal = [
  'products',
  'standing',
  'billingDates',
  'expirationDates',
  'needsCreditCardUpdate',
  'dateFirstSubscription',
  'scheduledChange',
  'trialExpiration',
];

const paidAccountFieldsAll = [
  ...paidAccountFieldsMinimal,
  'invoiceDetails',
  'canRenew',
  'contactLocale',
  'contactEmail',
  'contactFullName',
  'cardLast4',
  'cardType',
  'ixSubscriber',
  'zip',
  'country',
  'taxId',
  'stateTaxId',
  'trialType',
  'previousSubscription',
  'paidProduct',
  'productOverride',
].join(',');

const cardActions = [
  'addAttachmentToCard',
  'addChecklistToCard',
  'addMemberToCard',
  'commentCard',
  'copyCommentCard',
  'convertToCardFromCheckItem',
  'createCard',
  'copyCard',
  'deleteAttachmentFromCard',
  'emailCard',
  'moveCardFromBoard',
  'moveCardToBoard',
  'removeChecklistFromCard',
  'removeMemberFromCard',
  'updateCard:idList',
  'updateCard:closed',
  'updateCard:due',
  'updateCard:dueComplete',
  'updateCheckItemStateOnCard',
  'updateCustomFieldItem',
].join(',');

const boardActions = [
  cardActions,
  'addMemberToBoard',
  'addToOrganizationBoard',
  'copyBoard',
  'createBoard',
  'createCustomField',
  'createList',
  'deleteCard',
  'deleteCustomField',
  'disablePlugin',
  'disablePowerUp',
  'enablePlugin',
  'enablePowerUp',
  'makeAdminOfBoard',
  'makeNormalMemberOfBoard',
  'makeObserverOfBoard',
  'moveListFromBoard',
  'moveListToBoard',
  'removeFromOrganizationBoard',
  'unconfirmedBoardInvitation',
  'unconfirmedOrganizationInvitation',
  'updateBoard',
  'updateCustomField',
  'updateList:closed',
].join(',');

const card = {
  fields:
    'id,badges,closed,dueComplete,dateLastActivity,desc,descData,due,dueReminder,email,idBoard,idChecklists,idList,idMembers,idMembersVoted,idShort,idAttachmentCover,labels,idLabels,manualCoverAttachment,name,pos,shortLink,shortUrl,start,subscribed,url,cover,isTemplate,cardRole',
  stickers: true,
  sticker_fields: 'id,top,left,zIndex,rotate,image,imageUrl,imageScaled',
  attachments: true,
  attachment_fields:
    'id,bytes,date,edgeColor,fileName,idMember,isUpload,mimeType,name,pos,previews,url',
  customFieldItems: true,
  pluginData: true,
};

const currentBoardMinimal = {
  fields: boardFieldsFull,
  lists: 'open',
  list_fields:
    'name,closed,idBoard,pos,subscribed,limits,creationMethod,softLimit',
  cards: 'visible',
  card_fields: [cardFieldsBulk, 'labels'].join(','),
  card_attachments: 'cover',
  card_attachment_fields:
    'bytes,date,edgeColor,idMember,isUpload,mimeType,name,url',
  card_stickers: true,
  enterprise: true,
  enterprise_fields: 'displayName',
  members: 'all',
  member_fields: memberFields,
  memberships_orgMemberType: true,
  organization: true,
  organization_fields:
    'name,displayName,desc,descData,url,website,prefs,memberships,logoHash,products,limits,idEnterprise,premiumFeatures',
  organization_tags: true,
  organization_enterprise: true,
  organization_pluginData: true,
  myPrefs: true,
  pluginData: true,
  boardPlugins: true,
};

const currentBoardSecondary = {
  fields: 'id',
  actions: boardActions,
  actions_display: true,
  actions_limit: 50,
  action_memberCreator_fields: memberFieldsAndPremOrgsAdmin,
  action_reactions: true,
  cards: 'visible',
  card_fields: cardFieldsMinimal.join(','),
  card_checklists: 'all',
  card_checklist_fields: 'id,idBoard,pos,name,idCard',
  card_checklist_checkItems: 'none',
  labels: 'all',
  labels_limit: 1000,
};

const currentBoardPluginData = {
  fields: '',
  boardPlugins: true,
  cards: 'visible',
  card_fields: cardFieldsMinimal.join(','),
  card_attachments: true,
  card_attachment_fields:
    'bytes,date,edgeColor,idMember,isUpload,mimeType,name,url',
  card_customFieldItems: true,
  customFields: true,
  card_pluginData: true,
};

const currentBoardFull = {
  lists: 'open',
  list_fields:
    'name,closed,idBoard,pos,subscribed,limits,creationMethod,softLimit',
  cards: 'visible',
  card_stickers: true,
  card_fields: [cardFieldsBulk, 'labels'].join(','),
  enterprise: true,
  enterprise_fields: 'displayName',
  members: 'all',
  member_fields: memberFields,
  memberships_orgMemberType: true,
  organization: true,
  organization_fields:
    'name,displayName,desc,descData,url,website,prefs,memberships,logoHash,products,limits,idEnterprise,premiumFeatures',
  organization_tags: true,
  organization_enterprise: true,
  myPrefs: true,
  fields: boardFieldsFull,
  pluginData: true,
  organization_pluginData: true,
  boardPlugins: true,
  card_checklists: 'all',
  card_checklist_fields: 'id,idBoard,pos,name,idCard',
  card_checklist_checkItems: 'none',
  labels: 'all',
  labels_limit: 1000,
  card_attachments: true,
  card_attachment_fields:
    'bytes,date,edgeColor,idMember,isUpload,mimeType,name,url',
  card_customFieldItems: true,
  customFields: true,
  card_pluginData: true,
};

const memberBoards = {
  fields:
    'id,aaId,activityBlocked,avatarHash,avatarUrl,bio,bioData,confirmed,fullName,idEnterprise,idEnterprisesDeactivated,idMemberReferrer,idPremOrgsAdmin,initials,memberType,nonPublic,nonPublicAvailable,products,url,username,status,aaBlockSyncUntil,aaEmail,aaEnrolledDate,avatarSource,credentialsRemovedCount,domainClaimed,email,goldSunsetFreeTrialIdOrganization,gravatarHash,idBoards,idOrganizations,loginTypes,marketingOptIn,messagesDismissed,oneTimeMessagesDismissed,prefs,trophies,uploadedAvatarHash,uploadedAvatarUrl,premiumFeatures,isAaMastered,ixUpdate,limits,idEnterprisesAdmin',
  boards: 'open,starred',
  board_fields: boardFieldsMinimalSubscribed,
  board_organization: true,
  board_organization_fields: organizationFieldsMinimal,
  boardStars: true,
  credits: 'invitation,promoCode',
  organizations: 'all',
  organization_fields: organizationFieldsMinimalMemberships,
  organization_paidAccount: true,
  organization_paidAccount_fields: paidAccountFieldsMinimal.join(','),
  paidAccount: true,
  paidAccount_fields: paidAccountFieldsMinimal.join(','),
};

const memberAllBoards = {
  fields:
    'id,aaId,activityBlocked,avatarHash,avatarUrl,bio,bioData,confirmed,fullName,idEnterprise,idEnterprisesDeactivated,idMemberReferrer,idPremOrgsAdmin,initials,memberType,nonPublic,nonPublicAvailable,products,url,username,status,aaBlockSyncUntil,aaEmail,aaEnrolledDate,avatarSource,credentialsRemovedCount,domainClaimed,email,goldSunsetFreeTrialIdOrganization,gravatarHash,idBoards,idOrganizations,loginTypes,marketingOptIn,messagesDismissed,oneTimeMessagesDismissed,prefs,trophies,uploadedAvatarHash,uploadedAvatarUrl,premiumFeatures,isAaMastered,ixUpdate,limits,idEnterprisesAdmin',
  boards: 'all',
  board_fields: boardFieldsMinimalSubscribed,
  board_organization: true,
  board_organization_fields: organizationFieldsMinimal,
  boardStars: true,
  credits: 'invitation,promoCode',
  organizations: 'all',
  organization_fields: organizationFieldsMinimalMemberships,
  organization_paidAccount: true,
  organization_paidAccount_fields: paidAccountFieldsAll,
  paidAccount: true,
  paidAccount_fields: paidAccountFieldsAll,
};

const memberHeader = {
  fields: memberFieldsDefault.join(','),
  teamify: true,
  campaigns: true,
  channels: true,
  logins: true,
  organizations: 'all',
  organization_fields: 'name,displayName,idEnterprise,products',
  organization_paidAccount: true,
  organization_paidAccount_fields: paidAccountFieldsAll,
  organization_enterprise: true,
  paidAccount: true,
  paidAccount_fields: paidAccountFieldsAll,
  pluginData: true,
  savedSearches: true,
  enterpriseToExplicitlyOwnBoards: true,
  enterpriseLicenses: true,
  enterprises: true,
  enterprise_filter: ['saml', 'member', 'member-unconfirmed', 'owned'].join(
    ',',
  ),
  enterprise_fields:
    'name,displayName,isRealEnterprise,idAdmins,organizationPrefs',
  enterpriseWithRequiredConversion: true,
  ofEnterprise: true,
  ofEnterprise_fields: 'displayName,source',
};

const organizationBoardsPage = {
  boards: 'open',
  board_fields: boardFieldsInOrganization,
  board_starCounts: 'organization',
  board_membershipCounts: 'active',
  fields: organizationBoardsFields,
  paidAccount: true,
  paidAccount_fields: paidAccountFieldsAll,
  enterprise: true,
  memberships: 'active',
  members: 'all',
  tags: true,
};

const workspaceBoardsPageMinimal = {
  fields: organizationBoardsFields,
  boards: 'open',
  boards_count: 29,
  boards_sortBy: 'dateLastActivity',
  boards_sortOrder: 'desc',
  board_fields: boardFieldsInLargeOrganization,
  paidAccount: true,
  paidAccount_fields: paidAccountFieldsAll,
  enterprise: true,
  // right now our server doesn't support fields on enterprise :(. So leaving this commented out for now
  // enterprise_fields: enterpriseFields.join(','),
  tags: true,
  memberships: 'active',
};

const memberQuickBoards = {
  fields: 'idOrganizations',
  boards: 'open,starred',
  board_fields: boardFieldsMinimal,
  boardStars: true,
  organizations: 'all',
  organization_fields: 'idBoards',
};

const quickBoardsSearch = (search: string) => ({
  query: search,
  partial: true,
  modelTypes: 'boards',
  board_fields: boardFieldsMinimal,
});

const boardMinimalForDisplayCard = {
  lists: 'open',
  list_fields:
    'name,closed,idBoard,pos,subscribed,limits,creationMethod,softLimit',
  enterprise: true,
  enterprise_fields: 'displayName',
  members: 'all',
  member_fields: memberFields,
  memberships_orgMemberType: true,
  organization: true,
  organization_fields:
    'name,displayName,desc,descData,url,website,prefs,memberships,logoHash,products,limits,idEnterprise',
  organization_tags: true,
  organization_enterprise: true,
  organization_disable_mock: true,
  myPrefs: true,
  fields: boardFieldsFull,
  labels: 'all',
  labels_limit: 1000,
};

export interface QueryDefinition {
  [key: string]: string | boolean | number;
}

const _Queries: {
  boardFieldsInLargeOrganization: string;
  boardFieldsMinimal: string;
  boardFieldsFull: string;
  boardFieldsMinimalSubscribed: string;
  memberFields: string;
  memberFieldsAndPremOrgsAdmin: string;
  organizationFieldsMinimal: string;
  organizationFieldsMinimalMemberships: string;
  paidAccountFieldsMinimal: string;
  paidAccountFieldsAll: string;
  cardFieldsBulk: string;
  boardFieldsInOrganization: string;
  organizationBoardsFields: string;
  cardActions: string;
  boardActions: string;
  card: QueryDefinition;
  currentBoardMinimal: QueryDefinition;
  currentBoardSecondary: QueryDefinition;
  currentBoardPluginData: QueryDefinition;
  currentBoardFull: QueryDefinition;
  memberBoards: QueryDefinition;
  memberAllBoards: QueryDefinition;
  memberHeader: QueryDefinition;
  organizationBoardsPage: QueryDefinition;
  memberQuickBoards: QueryDefinition;
  quickBoardsSearch: (search: string) => QueryDefinition;
  boardMinimalForDisplayCard: QueryDefinition;
  workspaceBoardsPageMinimal: QueryDefinition;
} = {
  boardFieldsInLargeOrganization,
  boardFieldsMinimal,
  boardFieldsFull,
  boardFieldsMinimalSubscribed,
  memberFields,
  memberFieldsAndPremOrgsAdmin,
  organizationFieldsMinimal,
  organizationFieldsMinimalMemberships,
  paidAccountFieldsMinimal: paidAccountFieldsMinimal.join(','),
  paidAccountFieldsAll,
  cardFieldsBulk,
  boardFieldsInOrganization,
  organizationBoardsFields,
  cardActions,
  boardActions,
  card,
  currentBoardMinimal,
  currentBoardSecondary,
  currentBoardPluginData,
  currentBoardFull,
  memberBoards,
  memberAllBoards,
  memberHeader,
  organizationBoardsPage,
  memberQuickBoards,
  quickBoardsSearch,
  boardMinimalForDisplayCard,
  workspaceBoardsPageMinimal,
};

const shouldUseNewQueries = () =>
  getRecordedFeatureFlag(
    'fep.use-new-quick-queries',
    'false', // using strings to preserve the 'false' value in stored snapshot
  ) === 'true';

// eslint-disable-next-line @trello/no-module-logic
const Queries = shouldUseNewQueries()
  ? _Queries
  : { ...OldQueries, memberAllBoards: {} };

// eslint-disable-next-line import/no-default-export
export default Queries;
